<template>
	<div class="hris-page">
		<div class="hris-page-inner">
			<div class="hris-page-header">
				<span class="hris-page-eyebrow">Timekeeping</span>
				<h1 class="hris-page-title">TimeLogs</h1>
				<p class="hris-page-subtitle">Upload and review biometric time logs for the selected cut-off.</p>
			</div>

			<div class="hris-toolbar">
				<q-btn unelevated class="hris-q-outline" label="Template" @click="getTemplate()"></q-btn>
				<q-btn unelevated class="hris-q-outline" label="Upload Timelogs" @click="importEmployees"></q-btn>
				<div class="hris-toolbar-spacer"></div>
				<q-select v-model="yearfilter" :options="yearOptions" label="Year" outlined dense color="green" style="min-width: 140px; max-width: 200px;"></q-select>
				<q-select v-model="month" :options="monthOptions" label="Month" emit-value map-options option-value="id" option-label="name" outlined dense color="green" style="min-width: 160px; max-width: 200px;"></q-select>
				<q-select v-model="cutoff" :options="cutoffOptions" label="Cut-off" emit-value map-options option-value="id" option-label="name" outlined dense color="green" style="min-width: 160px; max-width: 200px;"></q-select>
				<q-btn unelevated class="hris-q-primary" label="Filter" @click="filterByDate"></q-btn>
			</div>

			<div class="hris-panel">
				<q-table :dense="$q.screen.lt.md" :grid="$q.screen.xs" :data="timelogs" :columns="columns"
					:pagination.sync="pagination" row-key="id" separator="cell"
					no-data-label="I didn't find anything for you!" :loading="loading" :filter="filter"
					@request="onRequest" binary-state-sort flat>

					<template v-slot:body="props">
						<q-tr slot="body" :props="props">
							<q-td key="employee_id" :props="props">{{ props.row.employee_id }}</q-td>
							<q-td key="name" :props="props" class="caps">{{ props.row.name }}</q-td>
							<q-td key="type" :props="props" class="caps">{{ props.row.type }}</q-td>
							<q-td key="time_logs" :props="props">{{ props.row.time_logs }}</q-td>
							<q-td key="image" :props="props">
								<q-avatar size="100px" square v-if="props.row.image" class="cursor-pointer" @click="showPreview(props.row.image)">
									<img :src="apiUrl + '/' + props.row.image">
								</q-avatar>
								<span v-else>No Image</span>
							</q-td>
						</q-tr>
					</template>
					<template v-slot:no-data>
						<div class="hris-empty">
							<span>The filter didn't uncover any results.</span>
						</div>
					</template>
					<template v-slot:loading>
						<q-inner-loading showing>
							<div class="hris-loading">
								<q-spinner color="green" size="40px"></q-spinner>
								<span>Loading timelogs...</span>
							</div>
						</q-inner-loading>
					</template>
					<template v-slot:top>
						<q-input dense outlined color="green" v-model="filter" placeholder="Search Employee">
							<template v-slot:append>
								<i class="fas fa-search"></i>
							</template>
						</q-input>
						<q-space></q-space>
						<q-btn unelevated class="hris-q-outline" label="Download Excel" style="margin-right: 10px;" no-caps @click="exportTable('xlsx')"></q-btn>
						<q-btn unelevated class="hris-q-outline" label="Download CSV" no-caps @click="exportTable('csv')"></q-btn>
					</template>
				</q-table>
			</div>
		</div>

		<q-dialog v-model="importfile" transition-show="scale" transition-hide="scale">
			<div class="hris-modal">
				<div class="hris-modal-header">
					<h3 class="hris-modal-title">Select File</h3>
				</div>
				<q-form ref="form" autocomplete="off" @submit="onSubmit">
					<div class="hris-modal-body">
						<p class="hris-form-section-hint" style="margin-top: 0;">.csv Only</p>
						<input type="file" id="file" ref="file" accept=".csv" :rules="[ val => val && val.length > 0 || 'This field is required!']"  v-on:change="handleFileUpload()">
					</div>
					<div class="hris-modal-footer">
						<q-btn unelevated class="hris-q-outline" label="Close" v-close-popup @click="closeModal"></q-btn>
						<q-btn unelevated class="hris-q-primary" label="Submit" type="submit"></q-btn>
					</div>
				</q-form>
			</div>
		</q-dialog>

		<q-dialog v-model="spinner" persistent>
			<div class="hris-loading" style="background: transparent; min-width: 120px;">
				<q-spinner color="green" size="48px"></q-spinner>
			</div>
		</q-dialog>

		<q-dialog v-model="previewDialog" transition-show="scale" transition-hide="scale">
			<div class="hris-modal">
				<div class="hris-modal-header">
					<h3 class="hris-modal-title">Image Preview</h3>
					<q-btn icon="fas fa-times" flat round dense v-close-popup></q-btn>
				</div>
				<div class="hris-modal-body text-center">
					<img :src="apiUrl + '/' + previewImage" style="max-width: 100%; max-height: 80vh; object-fit: contain;">
				</div>
			</div>
		</q-dialog>
	</div>
</template>

<script>
	import axios from 'axios';
	import mixins from '../../mixins';
	import { exportFile } from 'quasar';
	function wrapCsvValue (val, formatFn) {
		let formatted = formatFn !== void 0 ? formatFn(val) : val

		formatted = formatted === void 0 || formatted === null ? '' : String(formatted)

		formatted = formatted.split('"').join('""')
		return `"${formatted}"`
	}
	export default {
		data () {
			return {
				filter: '',
		        loading: false,
		        pagination: {
		            page: 1,
		            rowsPerPage: 15,
		            rowsNumber: 10
		        },

				yearfilter: new Date().getFullYear(),
				yearOptions: [],
				cutoff: 1,
				month: new Date().getMonth() + 1,

				importfile:false,
				spinner:false,
				file:'',
				previewDialog: false,
				previewImage: '',

				columns: [
				{ name: 'employee_id', align: 'center', label: 'Employee ID', field: 'employee_id', sortable: true },
				{ name: 'name', align: 'center', label: 'Fullname', field: 'name', sortable: true },
				{ name: 'type', align: 'center', label: 'Types', field: 'type', sortable: true },
				{ name: 'time_logs', align: 'center', label: 'Date', field: 'time_logs', sortable: true },
				{ name: 'image', align: 'center', label: 'Image', field: 'image', sortable: false },
				],
				timelogs: [],
				generatedData: [],
				cutoffOptions:[{id:1,name:'First Cut-off'},{id:2,name:'Second Cut-off'}],
				monthOptions:[
				{id:1,name:'January'},
				{id:2,name:'February'},
				{id:3,name:'March'},
				{id:4,name:'April'},
				{id:5,name:'May'},
				{id:6,name:'June'},
				{id:7,name:'July'},
				{id:8,name:'August'},
				{id:9,name:'September'},
				{id:10,name:'October'},
				{id:11,name:'November'},
				{id:12,name:'December'},],
				}
		},

		created(){
			this.yearsList()
		},

		mounted(){
	        this.onRequest({
	            pagination: this.pagination,
	            filter: ''
	        })
	    },

		methods: {

			//start if server side
			onRequest (props) {
		      const { page, rowsPerPage} = props.pagination
		      const filter = props.filter
		      this.loading = true
		      // emulate server
		      setTimeout(() => {
		        this.getRowsNumberCount(filter).then(total => {
		            this.pagination.rowsNumber = total
		        })
		        const fetchCount = rowsPerPage === 0 ? this.pagination.rowsNumber : rowsPerPage
		        const startRow = (page - 1) * rowsPerPage
		        this.fetchFromServer(startRow, fetchCount, filter)
		        this.pagination.page = page
		        this.pagination.rowsPerPage = rowsPerPage
		      }, 1500)
		    },

		    fetchFromServer (startRow, count, filter) {
		        axios.get(this.apiUrl + '/timelogs/display', {
		            headers: { 'Authorization': `Bearer ${this.token}` },
		            params: {
		              year: this.yearfilter,
		              month: this.month,
		              cutoff: this.cutoff,
		              start: startRow,
		              countPerPage: count,
		              filter: filter,
		            }
		        }).then((response) => {
		            this.timelogs = response.data.data
					this.generatedData = response.data.data
					this.loading = false
		        }).catch((error) => {
		            console.log(error);
		        })
		   },

		    async getRowsNumberCount (filter) {
		       let response = await axios.get(this.apiUrl + '/timelogs/display/total', {
		           headers: { 'Authorization': `Bearer ${this.token}` },
		           params: {
		             year: this.yearfilter,
		             month: this.month,
		             cutoff: this.cutoff,
		             filter: filter,
		           }
		       })
		       return response.data.data
		    },

			///end of server side

			importEmployees:function(){
				this.importfile = true
			},

			onSubmit:function(){
				this.spinner = true
				let formData  = new FormData();
				formData.append("files", this.file);
				axios({
					onUploadProgress: uploadEvent => {
						console.log("Progress: " + Math.round(uploadEvent.loaded / uploadEvent.total * 100) + '%')
					},
					headers : {'Authorization' : `Bearer ${this.token}`},
					url:this.apiUrl+'/timelogs/upload',
					data: formData,
					method: 'POST'
				})
				.then(response => {
					console.log(response.status)
					this.onRequest({pagination:this.pagination,filter:''})
					this.spinner = false
					this.importfile = false
				}).catch((error) => {
					console.log(error.response)
					this.spinner = false
					this.importfile = false
					this.$q.notify({
						message: 'Upload failed: ' + (error.response?.data?.message || 'Unknown error'),
						color: 'negative',
						icon: 'warning'
					})
				})
			},

			handleFileUpload(){
				this.file = this.$refs.file.files[0];
			},

			showPreview: function(image) {
				this.previewImage = image
				this.previewDialog = true
			},

			closeModal: function() {
				this.importfile = false
			},

			// displayTimelogs: function() {
			// 		this.spinner = true
			// 		axios.create({
			// 			baseURL: this.apiUrl,
			// 			headers: {
			// 				'Authorization': `Bearer ${this.token}`
			// 			}})
			// 		.get(`/timelogs/display/${this.yearfilter}/${this.month}/${this.cutoff}`)
			// 		.then((res) => {
			// 			//console.log(res.data.data)
			// 			this.timelogs = res.data.data;
			// 			this.initData = res.data.data;
			// 			setTimeout(() => {
			// 				this.spinner = false
			// 			},500)
			// 		})
			//
			// 	},

				filterByDate:function(){
					this.onRequest({pagination:this.pagination,filter:''})
				},

				getTemplate: function() {
					axios.create({
						baseURL: this.apiUrl,
						headers: {
							'Authorization': `Bearer ${this.token}`
						}})
					.get('/employee/template/download')
					.then((res) => {
						this.downloadTemplate(res.data.data.biometric_template)
					})
				},

				downloadTemplate:function(url){
					axios({
						url: this.apiUrl+'/'+url,
						method: 'GET',
						responseType: 'arraybuffer',
					}).then((response) => {
						var fileURL = window.URL.createObjectURL(new Blob([response.data]));
						var fileLink = document.createElement('a');
						fileLink.href = fileURL;
						fileLink.setAttribute('download', 'Attendance_template.csv');
						document.body.appendChild(fileLink);

						fileLink.click();
					});
				},


				yearsList : function(){
					axios.create({
						baseURL : this.apiUrl,
						headers : {'Authorization' : `Bearer ${this.token}`}
					}).post(`/timekeeping/years/list`)
					.then((res) => {
						this.yearOptions = res.data.data
					});
				},

				exportTable: async function (fileExtension) {
					this.loading = true
					try {
						const totalRows = await this.getRowsNumberCount(this.filter)
						const response = await axios.get(this.apiUrl + '/timelogs/display', {
							headers: { 'Authorization': `Bearer ${this.token}` },
							params: {
								year: this.yearfilter,
								month: this.month,
								cutoff: this.cutoff,
								start: 0,
								countPerPage: totalRows,
								filter: this.filter,
							}
						})
						const allData = response.data.data

						const content = [this.columns.map(col => wrapCsvValue(col.label)).join(',')].concat(
							allData.map(row => this.columns.map(col => wrapCsvValue(
								typeof col.field === 'function' ?
									col.field(row) :
									row[col.field === void 0 ? col.name : col.field],
								col.format
							)).join(','))
						).join('\r\n')

						const status = exportFile(
							`Timelogs.${fileExtension}`,
							content,
							`text/${fileExtension}`
						);

						if (status !== true) {
							this.$q.notify({
								message: 'Browser denied file download...',
								color: 'negative',
								icon: 'warning'
							})
						}
					} catch (error) {
						console.error(error)
						this.$q.notify({
							message: 'Error fetching data for export',
							color: 'negative',
							icon: 'warning'
						})
					} finally {
						this.loading = false
					}
				},
			}
		}
</script>
